"use client";

import { openModal } from "action/modal/openModal";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { IPageContent } from "api/interfaces/static-content.type";
import classNames from "classnames";
import ProductFilterSummary from "component/category/filter/ProductFilterSummary";
import Strike from "component/design-system/strike/strike";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import Text from "component/design-system/typography/Text";
import LoaderSkeleton from "component/loader-skeleton/LoaderSkeleton";
import OnScrollComponent from "component/on-scroll-component/OnScrollComponent";
import ProductRecentlyViewed from "component/product/recent-viewed-products/ProductRecentlyViewed";
import { RootState } from "interfaces/generics.type";
import Breadcrumbs from "modules/product/header/Breadcrumbs";
import RecentlyLikedSlider from "modules/recently-liked-slider/RecentlyLikedSlider";
import { DEFAULT_CATEGORY_BANNER } from "modules/site-structure/common/make-model-year-utils";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { filterSummaryAppliedFiltersHandler } from "util/category/filter-summary-applied-filters-handler";
import { isBusinessHP, isOEHP } from "util/helper/helper";
import { isMerchandiseCategory } from "util/product/apparel";
import { LINE_PAGES, TIER_MAKE_PAGES } from "util/product/product-constants";
import { getFragmentParams } from "util/url/getFragmentParams";
import showInteractiveVehicle from "util/vehicle/showInteractiveVehicle";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import { SlidersHorizontal } from "lucide-react";
import CategoryPagination from "../product/CategoryPagination";
import FilterTogglersLoader from "../product/filter-togglers/FilterTogglersLoader";
import useCategoryActions, { validPageNo } from "../product/useCategoryActions";
import CategoryTree from "./CategoryTree";
import FilterActionbar from "./FilterActionbar";
import OESearch from "./OESearch";
import stylesV2 from "./category-tree-v2.module.scss";
import BannerDescription from "./category-vehicle-banner-section/BannerDescription";
import CategoryVehicleSelector from "./category-vehicle-selector/CategoryVehicleSelector";
import ProductFilterContainer from "./filter/ProductFilterContainer";
import filterStyles from "./filter/new-product-filter.module.scss";
import InteractiveVehicle from "./interactive-vehicle/InteractiveVehicle";
import styles from "./ssrProductContainerRegular.module.scss";

const TabsCategory = dynamic(() => import(/* webpackChunkName: "tab-category" */ "modules/home/TabsCategory"));
const NoProductsRegular = dynamic(() => import(/* webpackChunkName: "no-products-placeholder-regular" */ "./NoProductsRegular"));
const ProductList = dynamic(() => import(/* webpackChunkName: "product-list" */ "component/product/product-list/product-list.component"), {
  loading: () => <LoaderSkeleton height={200} count={15} />,
});
const CategoryBannerRegular = dynamic(() => import(/* webpackChunkName: "category-banner-regular" */ "component/category/banner/CategoryBannerRegular"));
const NewPageHeader = dynamic(() => import(/* webpackChunkName: "new-page-header" */ "component/banner/NewPageHeader"), {
  loading: () => <LoaderSkeleton height={338} count={1} />,
});
const MetaInfoBar = dynamic(() => import(/* webpackChunkName: "meta-info-bar" */ "./meta-info-bar/MetaInfoBar"), {
  loading: () => <LoaderSkeleton height={65} count={1} />,
});

interface IProps {
  staticContent: IPageContent;
}

function SsrProductContainerRegular(props: IProps) {
  const {
    productList, immutableFilters, filterOptions, nbcFilterableAttributes, url,
    tiersWithUrls, appliedFilters, filterParams,
    pageDisplayableTitle, breadCrumbsV2, bannerData, breadcrumbData,
    fitmentOptionsDetails, fitmentOptionsPrompt,
    staticContent, categoryAggregation, isShopByCategory,
    pageStructureData,
  } = props;

  const { bannerDescription } = staticContent;

  const { t } = useTranslation("category");
  const { urlInformation } = useSelector((state: RootState) => state.utils);
  const { businessCustomer } = useSelector((store: RootState) => store.authenticationState);
  const isMerchandiseCat = isMerchandiseCategory(urlInformation);
  const { query = {}, asPath } = useRouter();
  const isBusinessPage = isBusinessHP(asPath);
  const isOEPage = isOEHP(asPath);

  const { year: fragmentYear, make: fragmentMake, model: fragmentModel } = getFragmentParams();

  const defaultYear = urlInformation.year || query.year || fragmentYear;
  const defaultMake = urlInformation.make || query.make || fragmentMake;
  const defaultModel = urlInformation.model || query.model || fragmentModel;

  const filterContainerParams = {
    filterOptions,
    immutableFilters,
    url,
    tiersWithUrls,
    appliedFilters,
    filterParams,
    garageTitle: pageDisplayableTitle,
    categoryAggregation,
  };

  const { layout: productLayout, currentPageType: pageType } = useSelector((state: RootState) => state.utils);
  const { selectedVehicle, vehicleAnnotations, vehicleImages } = useSelector((state: RootState) => state.garage);

  const isShowInteractiveVehicle = showInteractiveVehicle({
    selectedVehicle, breadcrumbData, vehicleAnnotations, vehicleImages,
  });

  const { onSelectPage } = useCategoryActions(url);

  const dispatch = useDispatch();

  const currentPage = validPageNo(appliedFilters.pageNumber);
  const renderLabelProps = {
    totalHits: productList.totalHits,
    currentPage,
    hasProducts: Array.isArray(productList.products) && productList.products.length,
  };

  const metaInfoRef = useRef();
  const categoryVehicleShowRef = useRef();

  const showCategories = categoryAggregation && categoryAggregation.tier0s && !isOEPage;
  const hasProducts = productList.products.length > 0;

  const { fitmentOptions = [] } = filterOptions;
  return (
    <div data-clarity-unmask="true">
      <HideIf show={!["MANUFACTURER_PAGE", ...TIER_MAKE_PAGES].includes(pageType) && !isBusinessPage}>
        <div className={styles.category_breadcrumb}>
          <Breadcrumbs
            {...{
              category: { ...breadcrumbData }, breadCrumbsV2, staticContent,
            }}
          />
        </div>
        <HideIf show={isMerchandiseCat || isOEPage}>
          <PAText component="h1" bold fontSize={FONT_SIZE[5]} className="m-b-15 m-t-0">
            {pageDisplayableTitle}
          </PAText>
        </HideIf>
      </HideIf>

      <HideIf show={!["MANUFACTURER_PAGE", ...TIER_MAKE_PAGES, ...LINE_PAGES].includes(pageType) && !businessCustomer && !isBusinessPage && !isMerchandiseCat && !isOEPage}>
        <NewPageHeader
          fitmentOptionsDetails={fitmentOptionsDetails}
          fitmentOptionsPrompt={fitmentOptionsPrompt}
          staticContent={staticContent}
          bannerData={bannerData}
          pageDisplayableTitle={pageDisplayableTitle}
          breadCrumbProps={{ category: { ...breadcrumbData }, breadCrumbsV2 }}
          showBreadCrumb={false}
        />
      </HideIf>

      <HideIf show={isMerchandiseCat || isOEPage}>
        <CategoryBannerRegular
          {...{
            staticContent,
            bannerData: {
              ...bannerData,
              fallbackImage: DEFAULT_CATEGORY_BANNER,
            },
            showBreadCrumb: false,
            // bannerProps,
            responsiveBannerMedia: staticContent.responsiveBannerMedia,
            applyStyle: false,
          }}
          desktopWidth={1450}
          desktopHeight={280}
        // maintainAspectRatio={!!bannerDescription}
        />
        <BannerDescription description={bannerDescription} className="m-t-15" />

      </HideIf>

      <HideIf show={isOEPage}>
        <OESearch />
      </HideIf>

      <div ref={categoryVehicleShowRef} />
      <HideIf show={hasProducts}>
        <div className={classNames("row m-l-0 m-r-0 p-lr-0 m-t-10", styles.product_row)}>

          <HideIf show={showCategories}>
            <div className={`hidden-sm col-md-3 p-lr-0 p-b-20 ${styles.sidebar}`}>
              <div className={classNames(styles.vehicle_info_wrapper, {
                [styles.fitment_margin]: fitmentOptions?.length > 0,
              })}
              >
                <HideIf show={!isMerchandiseCat && !isOEPage}>
                  <OnScrollComponent ref={categoryVehicleShowRef}>
                    <div className={classNames(styles.category_vehicle_selector, {
                      [styles.no_fitment_margin]: !fitmentOptions || fitmentOptions.length === 0,
                    })}
                    >
                      <CategoryVehicleSelector
                        defaultMake={defaultMake}
                        defaultModel={defaultModel}
                        defaultYear={defaultYear}
                      />
                    </div>
                  </OnScrollComponent>
                </HideIf>

                <div
                  className={classNames(styles.vehicle_selector_container, stylesV2.vehicle_selector_container_v2, {
                    "m-0": !validateVehicle(selectedVehicle),
                    "p-0": !validateVehicle(selectedVehicle),
                  })}
                >
                  <HideIf show={fitmentOptions && fitmentOptions.length > 0}>
                    <ProductFilterContainer
                      {...filterContainerParams}
                      filterOptions={{ fitmentOptions }}
                      categoryAggregation={categoryAggregation}
                      className={styles.product_filter}
                      customDropdown
                    />
                  </HideIf>
                </div>
              </div>

              <HideIf show={!isOEPage}>

                <div
                  data-testid="category-tree"
                  className={classNames(styles.category_tree_container, stylesV2.category_tree_container_v2)}
                >
                  <CategoryTree
                    tier0s={categoryAggregation?.tier0s}
                    appliedFilters={appliedFilters}
                    tiersWithUrls={tiersWithUrls}
                  />
                </div>
              </HideIf>

            </div>
          </HideIf>

          <div className={classNames("col-sm-12", styles.product_column, { "col-md-9": showCategories, "col-md-12": !showCategories })}>
            <HideIf show={isShopByCategory}>
              <TabsCategory />
            </HideIf>
            <HideIf show={!isShopByCategory}>

              <HideIf show={!businessCustomer}>
                <MetaInfoBar
                  garageTitle={pageDisplayableTitle}
                  pageType={pageType}
                  metaInfoRef={metaInfoRef}
                  appliedFilters={appliedFilters}
                  url={url}
                />
              </HideIf>

              <div className={`p-t-5 p-b-5 hidden-sm ${styles.category_filters}`}>
                <div className="d-flex flex-wrap align-items-center" data-testid="filters-container">
                  <ProductFilterContainer
                    {...filterContainerParams}
                    filterOptions={{ ...filterOptions, fitmentOptions: [] }}
                    nbcFilterableAttributes={nbcFilterableAttributes}
                    categoryAggregation={categoryAggregation}
                    maxFilters={4}
                  />
                  <div>
                    <div
                      className={`${filterStyles.option} d-flex align-items-center`}
                      onClick={() => dispatch(openModal("filter-sidebar", { ...filterContainerParams, nbcFilterableAttributes }))}
                    >
                      <SlidersHorizontal className="m-r-10 m-l-5" size={20} />
                      <PAText fontSize={FONT_SIZE[2]} color="black">{t("All Filters")}</PAText>
                    </div>
                  </div>
                  {/* <HideIf show={businessCustomer}>
                    <ProductFilterSortBy
                      value={sortBy}
                      onChange={onChangeSortBy}
                      className={styles.sort_by}
                    />
                  </HideIf> */}
                </div>
              </div>

              <FilterActionbar
                {...{
                  filterContainerParams: { ...filterContainerParams, isMobile: true },

                }}
                appliedFilters={appliedFilters}
                url={url}
                showSort={false}
              />

              <ProductFilterSummary
                renderLabelProps={renderLabelProps}
                tiersWithUrls={tiersWithUrls}
                disableFilters={immutableFilters}
                {...{
                  url, filterOptions, nbcFilterableAttributes,
                }}
                appliedFilters={filterSummaryAppliedFiltersHandler(appliedFilters)}
              />

              <HideIf show={hasProducts}>
                {/* Here when user search for a keyword (NOT SELECTED SUGGESTION i.e no context) we display result matching suggestion  */}

                <ProductList
                  showPlaceHolder
                  products={productList.products}
                  totalHits={productList.totalHits}
                  layout={productLayout}
                  defaultVehicle={{
                    defaultMake, defaultModel, defaultYear,
                  }}
                  fitmentOptionsDetails={fitmentOptionsDetails}
                />
              </HideIf>

              <>

                <div className="m-t-40">

                  <FilterTogglersLoader
                    {...{
                      filterOptions, productList, currentPage, url, appliedFilters,
                    }}
                  />
                </div>

                <HideIf show={hasProducts}>

                  <div>
                    <div className="m-t-20 d-flex align-item-center justify-content-center">

                      <CategoryPagination
                        {...{
                          productList, currentPage, onSelectPage, pageType,
                        }}
                      />
                    </div>
                  </div>
                  <hr style={{ borderColor: "#d7d7d7" }} />
                </HideIf>

              </>
              {/* </HideIf> */}
            </HideIf>
          </div>

        </div>
      </HideIf>
      <HideIf show={!hasProducts}>
        <NoProductsRegular />
        <div className="col-sm-12">
          <HideIf show={!isShopByCategory}>
            <Strike />
          </HideIf>
          <HideIf show={isShowInteractiveVehicle}>
            <InteractiveVehicle />
          </HideIf>
          <HideIf show={!isShowInteractiveVehicle}>
            <Text size={3} className="m-0 p-t-20 d-flex justify-content-center m-b-20" bold>
              {t("Search by top categories")}
            </Text>
            <TabsCategory />
          </HideIf>
        </div>
      </HideIf>
      <HideIf show={isShowInteractiveVehicle && hasProducts}>
        <InteractiveVehicle />
      </HideIf>
      {pageStructureData}
      <HideIf show={isBusinessPage}>
        <RecentlyLikedSlider />
      </HideIf>

      <div className="m-t-20 m-b-20">
        <ProductRecentlyViewed
          sideBarHandler={(data: ICompactProduct) => dispatch(openModal("sidebar", data))}
        />
      </div>
    </div>
  );
}

export default SsrProductContainerRegular;
